/**
 * #366 / #547 — Webview reload/crash recovery (shell ?b= cache bust).
 * Purges stale library/scan UI state, one authoritative refetch, then index IPC.
 */
import { shouldUseLocalLibraryIpc } from '../services/localLibraryIpcGate';
import {
  isLibraryScanInProgress,
  refreshConnectorScanWalkProgress,
} from '../services/libraryScanCoordinator';

let recoveryGateSatisfied = false;
let recoveryResyncPromise: Promise<void> | null = null;
let recoveryResyncDone = false;

export function isWebviewRecoveryNavigation(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (nav?.type === 'reload') return true;
  } catch {
    /* ignore */
  }
  try {
    if (new URL(window.location.href).searchParams.has('b')) return true;
  } catch {
    /* ignore */
  }
  return false;
}

/** @internal test hook */
export function resetLibraryIndexReloadGateForTests(): void {
  recoveryGateSatisfied = false;
  recoveryResyncPromise = null;
  recoveryResyncDone = false;
}

export type WebviewRecoveryGateResult = 'proceed' | 'defer_scan_active';

async function purgeLibraryUiCaches(): Promise<void> {
  console.info('[library-grid] webview recovery — purging cached library/scan UI state');

  try {
    localStorage.removeItem('maestro:libraryCache');
    localStorage.removeItem('maestro-library-cache');
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.removeItem('folderScanHistory');
  } catch {
    /* ignore */
  }

  const [
    { resetLocalAlbumIndex, isLocalAlbumIndexWarm },
    { clearAlbumWallSession },
    { resetScanAlbumPollClock },
    { resetScanProgressWatchdog },
    { invalidateLibraryBrowseCaches },
    { useLibraryCacheStore },
    { useLibraryStore },
    { resetScan },
  ] = await Promise.all([
    import('../services/libraryAlbumIndex'),
    import('../services/libraryAlbumWallSession'),
    import('../services/libraryAlbumScanPoll'),
    import('../services/scanProgressWatchdog'),
    import('../services/libraryBrowseCatalogCache'),
    import('../store/libraryCacheStore'),
    import('../store/libraryStore'),
    import('../services/libraryScanCoordinator'),
  ]);

  const indexWarm = isLocalAlbumIndexWarm();
  if (!indexWarm) {
    resetLocalAlbumIndex();
    clearAlbumWallSession();
  }
  resetScanAlbumPollClock();
  resetScanProgressWatchdog();
  invalidateLibraryBrowseCaches();
  useLibraryCacheStore.getState().clear();
  try {
    useLibraryStore.getState().invalidate();
  } catch {
    /* store optional */
  }
  resetScan();

  const { useOperationProgressStore } = await import('../store/operationProgressStore');
  for (const op of useOperationProgressStore.getState().getActive()) {
    useOperationProgressStore.getState().endOperation(op.id);
  }
}

async function fetchAuthoritativeLibrarySnapshot(): Promise<void> {
  const [{ shouldUseLocalLibraryIpc: ipcGate }, { libraryService }] = await Promise.all([
    import('../services/localLibraryIpcGate'),
    import('../services/libraryService'),
  ]);

  if (!ipcGate()) return;

  await refreshConnectorScanWalkProgress();
  try {
    await libraryService.getStats();
  } catch {
    /* counters stay hidden until LibraryBrowser refetch */
  }
}

/** One coordinated resync per webview recovery navigation. */
export async function runLibraryWebviewRecoveryResync(): Promise<void> {
  if (recoveryResyncDone) return;
  if (!isWebviewRecoveryNavigation()) {
    recoveryResyncDone = true;
    return;
  }
  if (recoveryResyncPromise) {
    await recoveryResyncPromise;
    return;
  }

  recoveryResyncPromise = (async () => {
    await purgeLibraryUiCaches();
    await fetchAuthoritativeLibrarySnapshot();
    recoveryResyncDone = true;
    if (typeof window !== 'undefined') {
      const { isLocalAlbumIndexWarm: indexWarmNow } = await import('../services/libraryAlbumIndex');
      window.dispatchEvent(new CustomEvent('maestro:library:webview-recovery', {
        detail: { reason: 'renderer_crash', forceFullRefresh: !indexWarmNow() },
      }));
      console.info('[library-grid] webview recovery resync complete — single refetch armed');
    }
  })();

  try {
    await recoveryResyncPromise;
  } finally {
    recoveryResyncPromise = null;
  }
}

/**
 * On webview reload/crash recovery: purge stale UI, probe scan status once before index IPC.
 * When a scan is still active, callers defer (scan poll / backoff owns the retry).
 */
export async function awaitCleanScanStatusAfterWebviewRecovery(
  signal?: AbortSignal,
): Promise<WebviewRecoveryGateResult> {
  if (!shouldUseLocalLibraryIpc()) return 'proceed';
  if (recoveryGateSatisfied) return 'proceed';
  if (!isWebviewRecoveryNavigation()) {
    recoveryGateSatisfied = true;
    return 'proceed';
  }

  if (signal?.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }

  await runLibraryWebviewRecoveryResync();

  if (isLibraryScanInProgress()) {
    console.info(
      '[library-grid] webview recovery — scan active after status check; deferring index load',
    );
    return 'defer_scan_active';
  }

  recoveryGateSatisfied = true;
  return 'proceed';
}
